import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { api, newKey } from "./client";

type Query = Record<string, string | number | boolean | undefined | null>;

/** GET with caching; the key is the path and its query so a filter change refetches. */
export function useGet<T>(
  path: string,
  query?: Query,
  opts: Partial<Pick<UseQueryOptions<T>, "enabled" | "staleTime" | "refetchInterval">> = {},
) {
  return useQuery<T>({
    queryKey: [path, query ?? {}] as QueryKey,
    queryFn: ({ signal }) => api.get<T>(path, { ...(query ? { query } : {}), signal }),
    ...opts,
  });
}

export type Method = "POST" | "PUT" | "PATCH" | "DELETE";

export interface MutationArgs<B> {
  body?: B;
  /** replaces `:param` segments of the path */
  params?: Record<string, string>;
  token?: string;
}

/**
 * A write: invalidates the listed query prefixes on success. Every mutation sends an Idempotency-Key created per click,
 * so a retried request replays instead of repeating (standards/api).
 */
export function useWrite<R = unknown, B = unknown>(method: Method, path: string, invalidate: string[] = []) {
  const qc = useQueryClient();
  return useMutation<R, Error, MutationArgs<B> | void>({
    mutationFn: async (args) => {
      const a = (args ?? {}) as MutationArgs<B>;
      const url = path.replace(/:(\w+)/g, (_m, k: string) => encodeURIComponent(a.params?.[k] ?? ""));
      return api.request<R>(method, url, {
        ...(a.body !== undefined ? { body: a.body } : {}),
        idempotencyKey: newKey(),
        ...(a.token ? { token: a.token } : {}),
      });
    },
    onSuccess: async () => {
      await Promise.all(
        invalidate.map((p) =>
          qc.invalidateQueries({
            predicate: (q) => typeof q.queryKey[0] === "string" && (q.queryKey[0] as string).startsWith(p),
          }),
        ),
      );
    },
  });
}

export interface PageOf<T> {
  items: T[];
  nextCursor?: string | null;
  total?: number;
}

/** Cursor-paged list ("Load more"); the filters are part of the key so a change starts again from the first page. */
export function useGetPages<T>(path: string, query: Query = {}, opts: { enabled?: boolean } = {}) {
  const q = useInfiniteQuery<PageOf<T>>({
    queryKey: [path, query, "pages"] as QueryKey,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      api.get<PageOf<T>>(path, {
        query: { ...query, ...(pageParam ? { cursor: pageParam as string } : {}) },
        signal,
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    ...(opts.enabled === undefined ? {} : { enabled: opts.enabled }),
  });
  const items = q.data?.pages.flatMap((p) => p.items);
  return { ...q, items, total: q.data?.pages[0]?.total };
}
